import type { PlayDialogDifficulty } from "../ui/dialog/play_dialog.js";
import type { LevelConfig, LevelLayer, TableConfig } from "../level_config.js";
// Leaf modules on purpose — see level_features.ts on why they may be imported
// at runtime from here while level_config may not.
import { readFeature } from "../level_features.js";
import { parsePayloadBarriers, parsePayloadLayers, parsePayloadElevator, parsePayloadHang, parsePayloadShape, parsePayloadTables, parsePayloadTilt, parsePayloadTopScale } from "../level_payload.js";

const PARAM = "test_level";

// Skips LevelPackService.refresh() at startup so gameplay uses the bundled
// LEVELS_CONFIG instead of whatever release the dev backend currently serves —
// for playtesting local level-content changes without a matching server release.
const LOCAL_LEVELS_PARAM = "local_levels";

const BATCH_PARAM = "stability_batch";

// Full bot-gameplay simulation of an editor batch (SimulationRunner over each level).
const SIM_PARAM = "sim_batch";
const SIM_RUNS_PARAM = "sim_runs";
const SIM_RUNS_DEFAULT = 200;

// Parallel shard windows are opened with `noopener` (each gets its OWN renderer
// process — opener-connected same-site windows share one), so they cannot
// postMessage the editor. They broadcast on this channel instead; a single
// "collector" window with an opener relays everything to the editor.
const SIM_CHANNEL_PARAM = "sim_channel";
const SIM_SHARD_PARAM = "sim_shard";
const SIM_COLLECTOR_PARAM = "sim_collector";

const DIFFICULTY_BY_NAME: Record<string, number> = {
  Common: 0,
  Hard: 1,
  SuperHard: 2,
};

export class TestLevelBridge
{
  private static parsed = false;
  private static config: LevelConfig | null = null;
  private static pending_single = false;
  private static batch: LevelConfig[] = [];
  private static batch_mode = false;
  private static sim_mode = false;
  private static sim_runs = SIM_RUNS_DEFAULT;
  private static sim_channel: string | null = null;
  private static sim_shard = 0;
  private static collector_mode = false;
  private static local_levels_mode = false;

  private static ensureParsed(): void
  {
    if ( this.parsed )
      return;

    this.parsed = true;

    try
    {
      const params = new URLSearchParams( window.location.search );

      this.local_levels_mode = params.get( LOCAL_LEVELS_PARAM ) !== null;
      this.sim_channel = params.get( SIM_CHANNEL_PARAM );
      this.sim_shard = Number( params.get( SIM_SHARD_PARAM ) ) || 0;

      if ( params.get( SIM_COLLECTOR_PARAM ) !== null )
      {
        this.batch_mode = true;
        this.sim_mode = true;
        this.collector_mode = true;
        return;
      }

      const sim_param = params.get( SIM_PARAM );
      const batch_param = sim_param !== null ? sim_param : params.get( BATCH_PARAM );
      if ( batch_param !== null )
      {
        this.batch_mode = true;
        this.sim_mode = sim_param !== null;

        const runs = Number( params.get( SIM_RUNS_PARAM ) );
        if ( Number.isFinite( runs ) && runs >= 1 )
          this.sim_runs = Math.min( 5000, Math.round( runs ) );

        if ( batch_param && batch_param !== "pending" )
        {
          this.batch = this.decodeBatch( batch_param );
          console.log( `[Stability] ${this.batch.length} рівнів з URL` );
        }
        return;
      }

      const encoded = params.get( PARAM );
      if ( !encoded )
        return;

      // Big cube-shaped levels no longer fit the URL at all — the editor sends
      // just this marker and hands the real payload over via postMessage once
      // we say we're ready (see awaitPendingSingle below), same idea as
      // stability_batch/sim_batch's own "pending" handshake above.
      if ( encoded === "pending" )
      {
        this.pending_single = true;
        return;
      }

      this.config = this.decode( encoded );

      if ( this.config )
      {
        console.log( `[TestLevel] Playing level ${this.config.level} from the editor.` );
        this.showBadge( this.config.level );
      }
    }
    catch ( e )
    {
      console.warn( "[TestLevel] Could not read the level from the URL", e );
      this.config = null;
    }
  }

  private static decodeJson( encoded: string ): string
  {
    const base64 = encoded.replace( /-/g, "+" ).replace( /_/g, "/" );
    return decodeURIComponent(
      atob( base64 )
        .split( "" )
        .map( c => "%" + c.charCodeAt( 0 ).toString( 16 ).padStart( 2, "0" ) )
        .join( "" )
    );
  }

  private static toConfig( raw: Record<string, unknown>, fallback_level: number ): LevelConfig | null
  {
    const parsed_level = Number( raw.level );
    const level = Number.isFinite( parsed_level ) ? parsed_level : fallback_level;

    const difficulty = ( DIFFICULTY_BY_NAME[String( raw.difficulty )] ?? 0 ) as PlayDialogDifficulty;

    const tables = this.parseTables( raw.tables );

    // Multi-table payload: `layers` in the payload is only the table #1 mirror,
    // so rebuild it from tables[0] and let GameScreen read `tables` for the rest.
    if ( tables.length > 0 )
      return { level, difficulty, layers: tables[0].layers, tables, ...parsePayloadTilt( raw ), ...parsePayloadShape( raw ), ...parsePayloadHang( raw ), ...parsePayloadElevator( raw ), ...parsePayloadTopScale( raw ), ...parsePayloadBarriers( raw, "TestLevel" ) };

    const layers = this.parseLayers( raw.layers );

    if ( layers.length === 0 )
    {
      console.warn( "[TestLevel] Payload has no layers" );
      return null;
    }

    return { level, difficulty, layers, ...parsePayloadTilt( raw ), ...parsePayloadShape( raw ), ...parsePayloadHang( raw ), ...parsePayloadElevator( raw ), ...parsePayloadTopScale( raw ), ...parsePayloadBarriers( raw, "TestLevel" ) };
  }

  private static parseTables( raw_tables_value: unknown ): TableConfig[]
  {
    return parsePayloadTables( raw_tables_value, "TestLevel" );
  }

  private static parseLayers( raw_layers_value: unknown ): LevelLayer[]
  {
    return parsePayloadLayers( raw_layers_value );
  }

  private static decode( encoded: string ): LevelConfig | null
  {
    const raw = JSON.parse( this.decodeJson( encoded ) ) as Record<string, unknown>;

    if ( !Number.isFinite( Number( raw.level ) ) )
    {
      console.warn( "[TestLevel] Payload has no level number" );
      return null;
    }

    return this.toConfig( raw, 1 );
  }

  private static decodeBatch( encoded: string ): LevelConfig[]
  {
    const raw = JSON.parse( this.decodeJson( encoded ) );
    if ( !Array.isArray( raw ) )
      return [];

    return raw
      .map( ( entry, index ) => this.toConfig( entry as Record<string, unknown>, index + 1 ) )
      .filter( ( c ): c is LevelConfig => c !== null );
  }

  public static getBatchLevels(): number[]
  {
    this.ensureParsed();
    return this.batch.map( c => c.level );
  }

  public static isBatchActive(): boolean
  {
    this.ensureParsed();
    return this.batch_mode;
  }

  public static isLocalLevelsForced(): boolean
  {
    this.ensureParsed();
    return this.local_levels_mode;
  }

  public static isSimBatch(): boolean
  {
    this.ensureParsed();
    return this.sim_mode;
  }

  public static getSimRuns(): number
  {
    this.ensureParsed();
    return this.sim_runs;
  }

  /**
   * The editor may hand out a different run count with each chunk of work: when
   * it has more windows than levels left it splits one level's runs across
   * several windows and merges the results. The URL parameter stays the default
   * for anything that does not say otherwise.
   */
  public static setSimRuns( runs: number ): void
  {
    this.ensureParsed();

    if ( !Number.isFinite( runs ) || runs < 1 )
      return;

    this.sim_runs = Math.min( 5000, Math.round( runs ) );
  }

  public static getSimChannel(): string | null
  {
    this.ensureParsed();
    return this.sim_channel;
  }

  public static getSimShard(): number
  {
    this.ensureParsed();
    return this.sim_shard;
  }

  public static isCollector(): boolean
  {
    this.ensureParsed();
    return this.collector_mode;
  }

  public static setBatch( raw: unknown ): number
  {
    this.ensureParsed();

    if ( !Array.isArray( raw ) )
      return 0;

    this.batch = raw
      .map( ( entry, index ) => this.toConfig( entry as Record<string, unknown>, index + 1 ) )
      .filter( ( c ): c is LevelConfig => c !== null );

    console.log( `[Stability] отримано ${this.batch.length} рівнів` );
    return this.batch.length;
  }

  private static batch_panel: HTMLDivElement | null = null;

  private static ensureBatchPanel(): HTMLDivElement
  {
    if ( this.batch_panel )
      return this.batch_panel;

    const panel = document.createElement( "div" );
    panel.style.cssText = [
      "position:fixed", "left:0", "top:0", "right:0", "bottom:0", "z-index:99999",
      "display:flex", "align-items:center", "justify-content:center",
      "background:#0b1220", "color:#cfe0ff", "text-align:center",
      "font:600 20px/1.5 system-ui,sans-serif", "pointer-events:none", "padding:24px",
      "white-space:pre-line",
    ].join( ";" );

    const attach = () => document.body?.appendChild( panel );
    if ( document.body )
      attach();
    else
      document.addEventListener( "DOMContentLoaded", attach );

    this.batch_panel = panel;
    return panel;
  }

  public static showBatchProgress( index: number, total: number ): void
  {
    this.ensureBatchPanel().textContent = `Перевірка фізики\n${index} з ${total}`;
  }

  public static showBatchText( text: string ): void
  {
    this.ensureBatchPanel().textContent = text;
  }

  public static showBatchSummary( stable: number, total: number ): void
  {
    const panel = this.ensureBatchPanel();
    panel.textContent = `Стійких ${stable} з ${total}\nРезультат надіслано в редактор — цю вкладку можна закрити`;
    panel.style.color = stable > 0 ? "#7ef0c2" : "#ffb3c0";
  }

  private static showBadge( level: number ): void
  {
    const badge = document.createElement( "div" );
    badge.textContent = `TEST · рівень ${level}`;
    badge.style.cssText = [
      "position:fixed",
      "left:8px",
      "top:8px",
      "z-index:99999",
      "padding:4px 8px",
      "border-radius:6px",
      "background:rgba(242,84,91,0.9)",
      "color:#fff",
      "font:600 12px/1.2 system-ui,sans-serif",
      "pointer-events:none",
    ].join( ";" );

    const attach = () => document.body?.appendChild( badge );
    if ( document.body )
      attach();
    else
      document.addEventListener( "DOMContentLoaded", attach );
  }

  public static isActive(): boolean
  {
    this.ensureParsed();
    return this.config !== null || this.batch_mode || this.pending_single;
  }

  public static isPendingSingle(): boolean
  {
    this.ensureParsed();
    return this.pending_single && this.config === null;
  }

  /**
   * Waits for the editor to hand over the real payload for a `test_level=pending`
   * URL — same idea as stability_check.ts's awaitBatch(), just for the plain
   * single-level flow instead of a headless batch. Resolves true once `config`
   * is set, false on timeout (editor never answered, or wasn't listening).
   */
  public static awaitPendingSingle( timeout_ms = 20_000 ): Promise<boolean>
  {
    this.ensureParsed();
    if ( !this.pending_single || this.config !== null )
      return Promise.resolve( this.config !== null );

    return new Promise( resolve =>
    {
      const timer = setTimeout( () =>
      {
        window.removeEventListener( "message", onMessage );
        console.warn( "[TestLevel] editor never sent the level payload" );
        resolve( false );
      }, timeout_ms );

      const onMessage = ( event: MessageEvent ) =>
      {
        const data = event.data as { type?: string; payload?: unknown } | null;
        if ( !data || data.type !== "brickbreaker-test-level" )
          return;

        clearTimeout( timer );
        window.removeEventListener( "message", onMessage );
        this.config = this.toConfig( data.payload as Record<string, unknown>, 1 );
        if ( this.config )
          this.showBadge( this.config.level );
        resolve( this.config !== null );
      };

      window.addEventListener( "message", onMessage );

      try
      {
        window.opener?.postMessage( { type: "brickbreaker-test-level-ready" }, "*" );
      }
      catch ( e )
      {
        console.warn( "[TestLevel] could not notify the editor of readiness", e );
      }
    } );
  }

  public static getLevel(): number | null
  {
    this.ensureParsed();
    return this.config?.level ?? null;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    this.ensureParsed();
    if ( this.config && this.config.level === level )
      return this.config;
    return this.batch.find( c => c.level === level ) ?? null;
  }

  public static restart(): void
  {
    window.location.reload();
  }
}
